import { value } from '@takeshape/util';
import isArray from 'lodash/isArray.js';
import isString from 'lodash/isString.js';
import { branding } from "../config.js";
import { hasEveryKey } from "../util/has.js";
const serviceProvider = {
    name: 'oidc',
    title: 'OpenID Connect',
    description: `OpenID Connect (OIDC) is an identity layer built on top of the OAuth 2.0 protocol and supported by some OAuth 2.0 providers, such as Google and Azure Active Directory. ${branding.productName} supports bearer tokens that have a JWKS endpoint available for validation.`,
    categories: ['authentication'],
    links: [
        {
            title: 'Homepage',
            url: 'https://swagger.io/docs/specification/authentication/openid-connect-discovery/'
        },
        { title: 'Docs', url: '/docs/services/providers/oidc' }
    ],
    serviceType: 'authentication',
    createId: value('oidc'),
    getFormSchema: value({
        type: 'object',
        properties: {
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9A-Za-z_-]+'
            },
            title: {
                title: 'Name',
                type: 'string'
            },
            issuer: {
                title: 'Issuer URL',
                type: 'string',
                format: 'url'
            },
            configurationUrl: {
                title: 'OpenID Configuration URL',
                type: 'string',
                format: 'url'
            },
            idClaim: {
                title: 'ID Claim',
                type: 'string',
                default: 'sub'
            },
            audience: {
                title: 'Audience',
                type: 'string'
            }
        },
        required: ['id', 'title', 'issuer', 'configurationUrl', 'idClaim']
    }),
    getFormConfig: value({
        order: ['title', 'issuer', 'configurationUrl', 'idClaim', 'audience'],
        properties: {
            audience: {
                widget: 'readonly',
                instructions: 'Click to copy, and then set as the audience in your issued token.',
                copy: true
            },
            title: {
                widget: 'singleLineText',
                instructions: 'Human-readable identifier for use in the UI.'
            },
            issuer: {
                widget: 'singleLineText'
            },
            configurationUrl: {
                widget: 'singleLineText',
                instructions: 'Must provide a valid, web-accessible **jwks_uri** entry.'
            },
            idClaim: {
                widget: 'singleLineText',
                instructions: 'A claim which can be used as a unique identifier for the token holder. **sub** is often a good value.'
            }
        }
    }),
    getConfig: (formData) => ({
        id: formData.id ?? 'oidc',
        title: formData.title ?? 'OpenID Connect Bearer Token',
        provider: 'oidc',
        serviceType: 'authentication',
        authenticationType: 'none',
        options: {
            configurationUrl: formData.configurationUrl,
            issuer: formData.issuer,
            audience: formData.audience,
            idClaim: formData.idClaim
        }
    }),
    getAuthJwtRequiredClaims: value(['sub']),
    getAuthIdClaim(config, payload) {
        const { idClaim } = config.options ?? {};
        if (!isString(idClaim)) {
            throw new Error('Incomplete service config. `idClaim` is required.');
        }
        const claim = payload[idClaim];
        if (!claim) {
            throw new Error(`Invalid JWT payload, claim ${idClaim} not found`);
        }
        return String(isArray(claim) ? claim.join('') : claim);
    },
    getAuthOpenIdConfig(config) {
        const { configurationUrl } = config.options ?? {};
        if (!configurationUrl) {
            throw new Error('Invalid service config. `configurationUrl` is required.');
        }
        return configurationUrl;
    },
    getAuthJwtVerifyConfig: ({ projectId }, config) => ({
        audience: `https://api.takeshape.io/project/${projectId}/${config.id}`,
        issuer: config.options?.issuer,
        algorithms: ['RS256']
    }),
    getAuthUserinfoEndpoint(_config, openidConfig) {
        return openidConfig.userinfo_endpoint;
    },
    getAuthAdditionalClaimsConfig(_config, userinfoUri, rawToken) {
        return {
            url: userinfoUri,
            headers: {
                authorization: `Bearer ${rawToken}`
            }
        };
    },
    isCompleteConfig: hasEveryKey(['options.configurationUrl', 'options.audience', 'options.idClaim'])
};
export default serviceProvider;
